import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { FilePlus2, Lightbulb, ArrowRight, Sparkles, ChevronDown, ChevronUp } from "lucide-react";

export default function Ideas() {
  const [ideas, setIdeas] = useState([]);
  const [curated, setCurated] = useState([]);
  const [loading, setLoading] = useState(true);
  // Curated preview shows just the first row (3 cards) by default so returning
  // clients can jump into their Ongoing Research without scrolling.
  const [curatedExpanded, setCuratedExpanded] = useState(false);

  useEffect(() => {
    Promise.all([
      api.get("/ideas").then((r) => setIdeas(r.data || [])).catch(() => setIdeas([])),
      api.get("/research-ideas", { params: { limit: 6 } }).then((r) => setCurated(r.data || [])).catch(() => setCurated([])),
    ]).finally(() => setLoading(false));
  }, []);

  return (
    <div data-testid="ideas-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted">ONGOING RESEARCH</div>
          <h1 className="font-serif text-3xl lg:text-4xl text-kb-ink mt-1">Your research <em className="italic text-kb-purple">projects & collaborations</em></h1>
        </div>
        <Link to="/dashboard/ideas/new" className="kb-btn-primary" data-testid="ideas-new-btn">
          <FilePlus2 className="w-4 h-4" /> Submit new idea
        </Link>
      </div>

      {/* Curated ideas preview — visible upfront, before any user idea exists */}
      {curated.length > 0 && (
        <section className="mt-8 kb-curated-card rounded-md p-5 lg:p-6" data-testid="ideas-curated-preview">
          <div className="flex items-end justify-between flex-wrap gap-3">
            <div>
              <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-purple inline-flex items-center gap-2"><Sparkles className="w-3 h-3" /> CURATED IDEAS</div>
              <h2 className="font-serif text-xl text-kb-ink mt-1">Need inspiration? Start from a mentor-curated idea.</h2>
            </div>
            <Link to="/dashboard/ideas/browse" className="kb-btn-primary text-sm" data-testid="ideas-browse-curated">
              Browse all curated <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>
          <div className="mt-5 grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {curated.slice(0, curatedExpanded ? curated.length : 3).map((c) => (
              <Link key={c.id} to={`/dashboard/ideas/browse`} className="bg-white border border-kb-divider rounded-md p-4 hover:border-kb-purple transition-colors" data-testid={`ideas-curated-card-${c.id}`}>
                <div className="flex items-center justify-between">
                  <Lightbulb className="w-4 h-4 text-kb-purple" />
                  <span className={`kb-mono text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-sm border ${c.is_premium ? "text-amber-700 bg-amber-50 border-amber-200" : "text-emerald-700 bg-emerald-50 border-emerald-200"}`}>
                    {c.is_premium ? "PREMIUM" : "FREE"}
                  </span>
                </div>
                <div className="font-serif text-sm text-kb-ink mt-3 leading-snug line-clamp-2">{c.title}</div>
                <div className="text-[11px] text-kb-ink-muted mt-1 line-clamp-2">{c.overview}</div>
              </Link>
            ))}
          </div>
          {curated.length > 3 && (
            <button
              type="button"
              onClick={() => setCuratedExpanded((v) => !v)}
              className="mt-4 kb-mono text-[10px] uppercase tracking-wider text-kb-primary inline-flex items-center gap-1 hover:underline"
              data-testid="ideas-curated-toggle"
            >
              {curatedExpanded ? (<><ChevronUp className="w-3 h-3" /> Show less</>) : (<><ChevronDown className="w-3 h-3" /> Show {curated.length - 3} more</>)}
            </button>
          )}
        </section>
      )}

      {loading ? (
        <div className="mt-8 kb-mono text-xs text-kb-ink-muted">Loading…</div>
      ) : ideas.length === 0 ? (
        <div className="mt-10 text-center py-20 border border-dashed border-kb-divider rounded-sm bg-white">
          <Lightbulb className="w-7 h-7 text-kb-ink-light mx-auto" strokeWidth={1.5} />
          <h3 className="font-serif text-xl text-kb-ink mt-4">No personal ideas yet</h3>
          <p className="text-sm text-kb-ink-muted mt-2 max-w-md mx-auto">
            Submit your first research idea — we&apos;ll generate a feasibility score, suggest a mentor and create a publication timeline.
          </p>
          <Link to="/dashboard/ideas/new" className="kb-btn-primary mt-6 text-sm inline-flex" data-testid="ideas-empty-cta">
            Submit an idea <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>
      ) : (
        <>
          <div className="mt-8 kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted">YOUR IDEAS</div>
          <div className="mt-3 grid gap-px bg-kb-divider border border-kb-divider rounded-sm overflow-hidden">
            {ideas.map((idea) => (
              <Link
                key={idea.id}
                to={`/dashboard/ideas/${idea.id}`}
                className="bg-white p-6 hover:bg-kb-subtle transition-colors flex flex-col lg:flex-row lg:items-center gap-4"
                data-testid={`idea-row-${idea.id}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2 kb-mono text-[10px] text-kb-ink-muted">
                    <span>{idea.id}</span>
                    <span>·</span>
                    <span className="uppercase">{idea.domain}</span>
                    {idea.target_quartile && <><span>·</span><span>TARGET {idea.target_quartile}</span></>}
                    {idea.is_collaborator && (
                      <span className="ml-1 kb-mono text-[9px] uppercase tracking-wider text-emerald-700 bg-emerald-50 border border-emerald-200 px-1.5 py-0.5 rounded-sm" data-testid={`idea-${idea.id}-collab-badge`}>
                        SHARED · COLLABORATOR
                      </span>
                    )}
                  </div>
                  <div className="font-serif text-lg text-kb-ink mt-1">{idea.title}</div>
                  <div className="text-sm text-kb-ink-muted mt-1 line-clamp-2">{idea.abstract}</div>
                </div>
                <div className="flex items-center gap-6">
                  <div>
                    <div className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-light">Feasibility</div>
                    <div className="font-mono text-kb-purple text-lg">{idea.feasibility_score ?? "—"}</div>
                  </div>
                  <span className="kb-mono text-[10px] uppercase text-kb-purple bg-kb-purple/5 border border-kb-purple/15 px-2 py-1 rounded-sm whitespace-nowrap">
                    {idea.status}
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
